import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { Buildings as BuildingsIcon } from "@phosphor-icons/react/Buildings";
import { CreditCard as CardIcon } from "@phosphor-icons/react/CreditCard";
import { Receipt as ReceiptIcon } from "@phosphor-icons/react/Receipt";
import { Banner } from "../../components/banner";
import type { Partner } from "../partner-templates/types";

export interface PartnerDashboardShellTemplateProps {
	partner: Partner | null;
	children?: React.ReactNode;
	onSignOut?: () => void;
	onOpenBillingInfo?: (url: string) => void;
	/** Navigates to the partner portfolio list (`/portfolios`) per spec §5.1. */
	onOpenPortfolio?: () => void;
	/** Highlights the "Client accounts" nav button when true. */
	isPortfolioActive?: boolean;
	/**
	 * Navigates to the in-app `/invoices` route per spec §5.4. The
	 * destination renders `PartnerInvoicesTemplate` — no Recurly URL is
	 * passed since the listing is fetched in-app via `Partner.invoices`.
	 */
	onOpenInvoices?: () => void;
	/** Highlights the "Invoices" nav button when true. */
	isInvoicesActive?: boolean;
	signedOut?: boolean;
}

export function PartnerDashboardShellTemplate({
	partner,
	children,
	onSignOut,
	onOpenBillingInfo,
	onOpenPortfolio,
	isPortfolioActive = false,
	onOpenInvoices,
	isInvoicesActive = false,
	signedOut = false,
}: PartnerDashboardShellTemplateProps): React.JSX.Element {
	const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);

	if (signedOut || !partner) {
		return (
			<Box
				sx={{
					minHeight: "100vh",
					bgcolor: "var(--mui-palette-background-default)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}>
				<Stack
					spacing={1}
					alignItems="center"
					sx={{ textAlign: "center" }}>
					<Typography variant="h6">You&apos;re signed out</Typography>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ maxWidth: 420 }}>
						Open manage-account sessions for any of your client accounts have
						been ended (per spec §3.8 / §10.1 — mechanism is an open engineering
						question; this surface only logs the cascade signal).
					</Typography>
				</Stack>
			</Box>
		);
	}

	const isSuspended = Boolean(partner.isSuspended);
	const hasCard =
		partner.hasPaymentMethodOnFile && partner.paymentMethodSummary;

	return (
		<Box
			sx={{
				minHeight: "100vh",
				bgcolor: "var(--mui-palette-background-default)",
				display: "flex",
				flexDirection: "column",
			}}>
			<AppBar
				position="static"
				elevation={0}
				sx={{
					bgcolor: "var(--mui-palette-background-paper)",
					color: "var(--mui-palette-text-primary)",
					borderBottom: "1px solid var(--mui-palette-divider)",
				}}>
				<Toolbar sx={{ gap: 2 }}>
					<Typography
						variant="h6"
						sx={{ flexGrow: 1 }}>
						{partner.companyName}
					</Typography>

					<Button
						size="small"
						color="secondary"
						variant={isPortfolioActive ? "contained" : "text"}
						startIcon={<BuildingsIcon size={16} />}
						onClick={() => onOpenPortfolio?.()}>
						Client accounts
					</Button>
					<Button
						size="small"
						color="secondary"
						variant={isInvoicesActive ? "contained" : "text"}
						startIcon={<ReceiptIcon size={16} />}
						onClick={() => onOpenInvoices?.()}>
						Invoices
					</Button>

					{hasCard ? (
						<Button
							size="small"
							color="secondary"
							variant="outlined"
							startIcon={<CardIcon size={16} />}
							onClick={() =>
								onOpenBillingInfo?.(partner.recurlyHostedBillingInfoUrl)
							}>
							{partner.paymentMethodSummary?.cardBrand} ••••{" "}
							{partner.paymentMethodSummary?.cardLastFour}
						</Button>
					) : (
						<Button
							size="small"
							variant="contained"
							color="warning"
							startIcon={<CardIcon size={16} />}
							onClick={() =>
								onOpenBillingInfo?.(partner.recurlyHostedBillingInfoUrl)
							}>
							Set payment method
						</Button>
					)}

					<IconButton
						size="small"
						onClick={(e) => setMenuAnchor(e.currentTarget)}>
						<Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
							{partner.firstName[0]}
							{partner.lastName[0]}
						</Avatar>
					</IconButton>
					<Menu
						anchorEl={menuAnchor}
						open={Boolean(menuAnchor)}
						onClose={() => setMenuAnchor(null)}>
						<MenuItem
							onClick={() => {
								setMenuAnchor(null);
								onSignOut?.();
							}}>
							Sign out
						</MenuItem>
					</Menu>
				</Toolbar>
			</AppBar>

			{isSuspended && (
				<Banner
					severity="error"
					title="Your partner account is suspended"
					description="Commercial actions are disabled. Existing client account content can still be managed. Contact support to restore commercial access."
				/>
			)}

			{!hasCard && !isSuspended && (
				<Banner
					severity="warning"
					title="No payment method on file"
					description="Set a payment method before creating client accounts or activating trials to paid."
					action={{
						label: "Set payment method",
						onClick: () =>
							onOpenBillingInfo?.(partner.recurlyHostedBillingInfoUrl),
					}}
				/>
			)}

			<Box sx={{ flex: 1, p: { xs: 2, md: 4 } }}>{children}</Box>
		</Box>
	);
}
